import { useTranslation } from 'next-i18next';
import { useFela } from 'react-fela';
import { connect } from 'react-redux';
import { Icon } from '@bridebook/aura';
import { HeartOutline } from '@bridebook/aura/src/icons';
import { Box, Button } from '@bridebook/ui';
import { IconBurger } from '@bridebook/ui/src/icons';
import { IconEdit } from '@bridebook/ui/src/icons/dynamic';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import TopBarHeaderItem from '@bridebook/web/components/app/top-bar/top-bar-header-item';
import TopBarInboxIcon from '@bridebook/web/components/app/top-bar/top-bar-inbox-icon';
import { NavigateToSearchButton } from '@bridebook/web/components/app/top-bar/navigate-to-search-button';
import { ConnectedTypes, IApplicationState } from '@bridebook/web/lib/types';
import { toggleNavMenuDrawer } from '@bridebook/web/lib/ui/actions';
import { getMenu } from '@bridebook/web/lib/ui/menu';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { isHomePage } from '@bridebook/web/lib/utils/url';
import componentStyles from './top-bar-menu-buttons.style';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { useMemo } from 'react';

const storeEnhancer = connect((state: IApplicationState) => ({
  isMobile: getScreenType(state).isMobile,
  isTablet: getScreenType(state).isTablet,
  isDesktop: getScreenType(state).isDesktop,
  isHomePage: isHomePage(state.app.pathname),
}));

interface IProps extends ConnectedTypes<typeof storeEnhancer> {}

const TopBarMenuButtons = ({ isMobile, isTablet, isDesktop, dispatch, isHomePage }: IProps) => {
  const { t } = useTranslation('common');
  const { css } = useFela();

  const thisToggleDrawer = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(toggleNavMenuDrawer(true));
  };

  const styles = componentStyles({ isMobile });

  const menu = useMemo(() => getMenu(), []);

  if (isMobile) {
    const backIconWidth = isMobile ? 22 : 14;

    return (
      <Box style={styles.wrapper} data-name="top-bar-menu-buttons">
        {isHomePage && !isDesktop && (
          <Link href="/shortlist" passHref>
            <Button
              theme="ghost"
              noUnderline
              color="white"
              backIcon={<Icon icon={HeartOutline} size={backIconWidth} className="text-white" />}
              style={styles.button}
            />
          </Link>
        )}
        {isHomePage && (
          <Link href="/settings" passHref>
            <Button
              theme="ghost"
              noUnderline
              color="white"
              backIcon={<IconEdit width={backIconWidth} />}
              text={isDesktop ? t('topBar.edit') : undefined}
              style={styles.button}
            />
          </Link>
        )}

        <Box style={styles.stretchWrap}></Box>

        <TopBarInboxIcon />

        <Box style={styles.menuButtonWrap}>
          <Button
            name="topbar-burger-menu-button"
            dataTest="topbar-burger-menu-button"
            theme="ghost"
            noUnderline
            color="white"
            backIcon={<IconBurger width={backIconWidth} />}
            text={isDesktop ? t('topBar.weddingPlanner') : undefined}
            onClick={thisToggleDrawer}
            style={styles.button}
          />
        </Box>
      </Box>
    );
  } else {
    const showTopHeaderItems = isDesktop || isTablet;
    const backIconWidth = isMobile ? 24 : 16;

    return (
      <Box style={styles.wrapper} data-name="top-bar-menu-buttons">
        <div className={css(styles.menuButtons)}>
          {isHomePage && !isDesktop && !isTablet && (
            <Link href="/shortlist" passHref>
              <Button
                theme="ghost"
                noUnderline
                color="white"
                backIcon={<Icon icon={HeartOutline} size={backIconWidth} className="text-white" />}
                style={styles.button}
              />
            </Link>
          )}
        </div>

        <Box style={styles.stretchWrap}></Box>
        <div className={css(styles.menuButtons)}>
          <TopBarInboxIcon />

          {showTopHeaderItems && (
            <div className={css(styles.iconPadding)}>
              <NavigateToSearchButton />
            </div>
          )}
          {showTopHeaderItems && (
            <>
              <div className={css(styles.iconPadding)}>
                <TopBarHeaderItem
                  href={UrlHelper.shortlist.main}
                  label={menu.shortlist.title}
                  icon="favourites"
                  dataName="top-bar-shortlist-icon"
                />
              </div>
            </>
          )}
          <Button
            name="topbar-burger-menu-button"
            dataTest="topbar-burger-menu-button"
            theme="ghost"
            noUnderline
            color="white"
            backIcon={<IconBurger width={backIconWidth} />}
            text={isDesktop ? t('topBar.menu') : undefined}
            onClick={thisToggleDrawer}
            style={styles.button}
          />
        </div>
      </Box>
    );
  }
};

export default storeEnhancer(TopBarMenuButtons);
